import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../lib/api";
import { imageUrl } from "../lib/cloudinary";
import ProductStrip from "../components/ProductStrip";
import HeroCarousel from "../components/HeroCarousel";
import AdVideoSection from "../components/AdVideoSection";
import ErrorBoundary from "../components/ErrorBoundary";
import Reveal from "../components/Reveal";
import {
  ArrowRight, Phone, MessageCircle, Mail, Plus, Minus,
} from "lucide-react";
import { CONTACT, waUrl, waEnquiryUrl, useContactNumber, useContactEmail } from "../lib/contact";
import {
  HERO_SLIDES, CATEGORIES, WHY, FAQ, STORY_PORTRAIT_IMG, STORY_PORTRAIT_IMG_FALLBACK, BUNDLED_BESTSELLER_NAMES,
} from "../lib/homeContent";
import { useSettings } from "../contexts/SettingsContext";
import { SITE_ORIGIN, ORGANIZATION_JSON_LD, categoryPathFor } from "../lib/seo";
import SEOHead from "../components/SEOHead";

/**
 * iter86/iter87 — Robust "Our Story" portrait.
 *
 * Fixes the production bug where the ammamma image never rendered:
 *   • The original `<img loading="lazy">` sat inside a framer-motion Reveal
 *     wrapper.  With no explicit `width`/`height`, the img box was 0px tall,
 *     so Chrome/Safari's lazy-load heuristic never fetched it.
 *   • The source PNG on the emergent CDN was 2.4 MB — unacceptable payload
 *     that timed-out on slow client networks.
 *
 * How this component is future-proof (iter87 hardening):
 *   1. `loading="eager"` + `fetchpriority="high"` — the browser is TOLD to
 *      load this image on first paint; no lazy-load quirks can suppress it.
 *   2. Explicit `width` / `height` attributes → the aspect ratio is
 *      reserved so the layout doesn't collapse to 0-height and the browser
 *      always classifies this element as an intentional visual.
 *   3. `imageUrl(..., { width: 900 })` routes through Cloudinary's
 *      `image/fetch` transform — the source is re-encoded to WebP/AVIF
 *      and served from the global CDN.  Falls back to the raw URL if
 *      Cloudinary config hasn't loaded yet.
 *   4. THREE-tier `onError` fallback:
 *         optimised URL → raw NEW URL → legacy URL → branded SVG.
 *      No broken-image icon, no alt-text-only state ever visible.
 *   5. `<link rel="preload" as="image">` in Home's <Helmet> starts the
 *      request the moment the HTML parses, well before React mounts.
 *   6. `console.warn` on any failure step so failed fetches surface in
 *      the browser console + our error tracker for future debugging.
 */
function StoryPortrait() {
  const [failed, setFailed] = useState(false);
  const optimised = imageUrl(STORY_PORTRAIT_IMG, { width: 900, crop: "fit" });

  if (failed) {
    return (
      <figure
        className="relative w-full max-w-2xl aspect-[7/6] rounded-3xl overflow-hidden bg-gradient-to-br from-brand-chili/20 via-brand-dark to-brand-turmeric/10 border border-white/10 flex items-center justify-center"
        data-testid="our-story-portrait-fallback"
      >
        <svg viewBox="0 0 200 200" className="w-2/5 h-2/5 text-brand-turmeric/80" fill="currentColor" aria-hidden="true">
          <path d="M100 20a40 40 0 1 1 0 80 40 40 0 0 1 0-80zm-72 160a72 72 0 0 1 144 0v4H28v-4z" />
        </svg>
        <figcaption className="absolute bottom-6 left-6 right-6 text-center text-sm text-brand-cream/70 tracking-wide">
          Handcrafted in Andhra since 2002
        </figcaption>
      </figure>
    );
  }

  return (
    <figure
      className="relative w-full max-w-2xl rounded-3xl overflow-hidden bg-brand-cream/5 border border-white/10 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.55)]"
      style={{ aspectRatio: "7 / 6", minHeight: "260px" }}
    >
      <img
        src={optimised || STORY_PORTRAIT_IMG}
        alt="Ammamma preparing traditional Andhra pickle by hand"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        width="900"
        height="775"
        className="absolute inset-0 w-full h-full object-cover block"
        data-testid="our-story-portrait"
        onError={(e) => {
          const src = e.currentTarget.src;
          console.warn("[StoryPortrait] image failed to load:", src);
          // Tier 1: Cloudinary-optimised failed → try raw NEW URL directly.
          if (src !== STORY_PORTRAIT_IMG && src !== STORY_PORTRAIT_IMG_FALLBACK) {
            e.currentTarget.src = STORY_PORTRAIT_IMG;
            return;
          }
          // Tier 2: raw NEW URL failed → try the legacy fallback URL.
          if (src === STORY_PORTRAIT_IMG && STORY_PORTRAIT_IMG_FALLBACK && STORY_PORTRAIT_IMG_FALLBACK !== STORY_PORTRAIT_IMG) {
            e.currentTarget.src = STORY_PORTRAIT_IMG_FALLBACK;
            return;
          }
          // Tier 3: everything failed → show the branded SVG placeholder.
          setFailed(true);
        }}
      />
    </figure>
  );
}

function FAQItem({ item, index, open, onToggle }) {
  return (
    <div className="border-b border-brand-line" data-testid={`faq-item-${index}`}>
      <button onClick={onToggle} className="w-full text-left py-5 flex items-center justify-between gap-4 group">
        <span className="font-serif text-lg sm:text-xl text-brand-dark pr-4 transition-colors group-hover:text-brand-chili">{item.q}</span>
        {open ? <Minus className="w-5 h-5 text-brand-chili flex-shrink-0" strokeWidth={1.5} /> : <Plus className="w-5 h-5 text-brand-muted flex-shrink-0" strokeWidth={1.5} />}
      </button>
      <div className={`grid transition-all duration-500 ease-out ${open ? "grid-rows-[1fr] pb-5" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          {item.aLines ? (
            <ul className="space-y-2 max-w-3xl" data-testid={`faq-answer-list-${index}`}>
              {item.aLines.map((line, i) => (
                <li key={i} className="text-sm text-brand-muted leading-relaxed">
                  <span className="font-semibold text-brand-dark">{line.label}</span>{" "}
                  <span>{line.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-brand-muted leading-relaxed max-w-3xl">{item.a}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [faqOpen, setFaqOpen] = useState(-1);
  const navigate = useNavigate();
  const { settings, settingsLoaded } = useSettings();
  // iter53 — live admin-editable call number.
  const contact = useContactNumber();
  // iter71 — customer email live-editable via Admin → Settings.
  const email = useContactEmail();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  // iter75 — Hero slides are now FULLY DYNAMIC:
  //   • settings.hero_slides === null/undefined  → first-load defaults (bundled HERO_SLIDES)
  //   • settings.hero_slides === []              → admin cleared → render nothing
  //   • settings.hero_slides === [...]           → render EXACTLY what admin saved,
  //                                                in the saved order.
  // The previous merge-by-id logic was silently reviving deleted slides
  // and ignoring the admin's reorder because it iterated the bundled
  // HERO_SLIDES first.  No merge is done here anymore.
  const heroSlides = (() => {
    const saved = settings?.hero_slides;
    if (!Array.isArray(saved)) return HERO_SLIDES;
    if (saved.length === 0) return [];
    return saved.map((o) => ({
      id: o.id,
      img: o.img,
      img_desktop: o.img_desktop || "",
      alt: o.alt || "",
      overline: o.overline || "",
      title: o.title || "",
      subtitle: o.subtitle || "",
      ctaLabel: o.cta_label || "",
      ctaHref: o.cta_href || "",
    }));
  })();

  const findByName = (name) => products.find((p) => p.name.toLowerCase() === name.toLowerCase());
  // iter42: Crowd Favourites — admin-curated list overrides the hardcoded one.
  //   Admin selects products via /admin/homepage → "Crowd Favourites".  When
  //   they save, settings.bestseller_product_ids holds product_ids in the
  //   order they appear.  We resolve them against the live products list so
  //   out-of-stock/deleted ones silently vanish.
  const adminBestsellers = Array.isArray(settings?.bestseller_product_ids) ? settings.bestseller_product_ids : [];
  const bestsellers = adminBestsellers.length > 0
    ? adminBestsellers.map((pid) => products.find((p) => p.product_id === pid)).filter(Boolean)
    : BUNDLED_BESTSELLER_NAMES.map(findByName).filter(Boolean);

  // iter42: Shop By Category — admin-curated list overrides bundled CATEGORIES.
  //   `homepage_categories` items: { name, image_url, enabled, order, unit? }.
  //   The `icon` (React component) cannot round-trip through Mongo, so we
  //   resolve it from the bundled CATEGORIES by name on each render —
  //   without this lookup, `<c.icon />` would crash the homepage when the
  //   admin saves any homepage setting (because `homepage_categories` is
  //   always persisted as plain objects without icons).
  //
  // iter68 — Combos auto-image: the Combos category ships with no bundled
  // artwork (per business decision).  If the admin hasn't uploaded a
  // dedicated tile image, we fall back to the first combo product's main
  // image so the card looks native.  If NEITHER exists yet, the tile is
  // filtered out entirely (below) so an empty-looking card is never shown.
  const combosFirstImage = (products.find((p) => p.category === "Combos" && p.image_url) || {}).image_url || "";
  const adminCats = Array.isArray(settings?.homepage_categories) ? settings.homepage_categories : [];
  const homeCategories = (adminCats.length > 0
    ? adminCats
        .filter((c) => c && c.name && c.enabled !== false)
        .slice()
        .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
        .map((c) => {
          const bundled = CATEGORIES.find((b) => b.name === c.name);
          const img = c.image_url || bundled?.img || (c.name === "Combos" ? combosFirstImage : "");
          return {
            name: c.name,
            unit: c.unit || bundled?.unit || "products",
            img,
            icon: bundled?.icon || null,
          };
        })
    : CATEGORIES.map((c) => (
        c.name === "Combos" && !c.img ? { ...c, img: combosFirstImage } : c
      ))
  ).filter((c) => c.name !== "Combos" || Boolean(c.img));

  return (
    <div data-testid="home-page" className="overflow-hidden">
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={imageUrl(STORY_PORTRAIT_IMG, { width: 900, crop: "fit" }) || STORY_PORTRAIT_IMG}
          fetchPriority="high"
        />
        <script type="application/ld+json">{JSON.stringify(ORGANIZATION_JSON_LD)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "WebSite",
          "name": "Sri Durga Foods",
          "url": SITE_ORIGIN,
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${SITE_ORIGIN}/shop?search={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        })}</script>
      </Helmet>
      <SEOHead
        title="Sri Durga Foods | Authentic Andhra Pickles, Spices & Wood-Pressed Oils"
        description="Sri Durga Foods — handcrafted Andhra pickles (Avakaya, Gongura), culinary pastes, masalas, podis, wood-pressed oils and flours. 25+ years of Nellore tradition, delivered pan-India."
        canonical="/"
      />
      {/* HERO CAROUSEL — iter51: do NOT render until /api/settings has
          resolved at least once. This prevents the bundled 1:1 defaults
          from flashing in for ~200ms before the admin-saved 5:7 images
          arrive. We render a same-height black placeholder during the
          loading window so the page layout doesn't jump.
          • settingsLoaded === false → show black placeholder (no images)
          • settingsLoaded === true + heroSlides.length > 0 → carousel
          • settingsLoaded === true + heroSlides.length === 0 → render nothing
            (admin explicitly cleared all slides). */}
      {!settingsLoaded ? (
        <div
          className="h-[68vh] min-h-[460px] sm:h-[78vh] sm:min-h-[560px] lg:h-[92vh] lg:min-h-[680px] bg-brand-dark"
          aria-hidden="true"
          data-testid="hero-placeholder"
        />
      ) : (
        heroSlides.length > 0 && (
          <ErrorBoundary fallback={null}>
            <HeroCarousel slides={heroSlides} intervalMs={3000} />
          </ErrorBoundary>
        )
      )}

      {/* ADVERTISEMENT VIDEO */}
      <ErrorBoundary fallback={null}>
        <AdVideoSection />
      </ErrorBoundary>

      {/* CROWD FAVOURITES (bestsellers) */}
      <ErrorBoundary>
      <section className="container-x py-16 sm:py-24 lg:py-32" data-testid="bestsellers-section">
        <div className="mb-8 sm:mb-12">
          <span className="overline mb-3 block">Crowd Favourites</span>
          <h2 className="font-serif text-3xl sm:text-5xl text-brand-dark leading-tight max-w-xl">
            Bestsellers, by popular<br />demand.
          </h2>
        </div>
        <ProductStrip products={bestsellers} testId="bestsellers" columnsLg={5} />
      </section>
      </ErrorBoundary>

      {/* SHOP BY CATEGORY */}
      <ErrorBoundary>
      <section className="bg-brand-subtle py-20 sm:py-24 lg:py-32" data-testid="categories-section">
        <div className="container-x">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="overline mb-3 block">Shop by Category</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-brand-dark leading-tight">From the field to your kitchen shelf.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {homeCategories.map((c) => {
              // null/undefined → use bundled default image. Empty string ""
              // → admin explicitly removed the image → show a small "no image"
              // placeholder so the customer doesn't see a broken default.
              const ovMap = settings?.category_images;
              const hasOverride = ovMap && typeof ovMap === "object" && (c.name in ovMap);
              const override = hasOverride ? ovMap[c.name] : null;
              const imgSrc = hasOverride ? (override || "") : c.img;
              // Live product count for this category — recomputed whenever
              // the products list changes (admin add/delete/move/disable).
              const liveCount = products.filter((p) => p.category === c.name && p.in_stock !== false).length;
              // "1 Flour" not "1 Flours" — drop the trailing 's' on singular
              // when the unit ends with 's'.
              const unitLabel = (liveCount === 1 && c.unit.endsWith("s"))
                ? c.unit.replace(/s$/, "")
                : c.unit;
              const countLabel = `${liveCount} ${unitLabel}`;
              return (
              <Link
                key={c.name}
                to={`/${categoryPathFor(c.name)}`}
                className="group relative bg-white border border-brand-line rounded-2xl overflow-hidden hover:border-brand-chili/40 hover:-translate-y-1 transition-all duration-500"
                data-testid={`category-${c.name.toLowerCase()}`}
              >
                <div className="aspect-[4/5] bg-brand-cream overflow-hidden">
                  {imgSrc ? (
                    <img src={imageUrl(imgSrc, { width: 600, height: 600, crop: "fill" })} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-brand-muted/60">No image</div>
                  )}
                </div>
                <div className="p-5">
                  {c.icon && <c.icon className="w-5 h-5 text-brand-chili mb-2" strokeWidth={1.3} />}
                  <h3 className="font-serif text-2xl text-brand-dark leading-tight">{c.name}</h3>
                  <span className="overline !text-brand-muted text-[10px] mt-1 block">{countLabel}</span>
                </div>
                <span className="absolute top-4 right-4 w-8 h-8 rounded-full bg-brand-dark text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </span>
              </Link>
            );})}
          </div>
        </div>
      </section>
      </ErrorBoundary>

      {/* OUR STORY */}
      <section className="bg-brand-dark text-brand-cream py-20 sm:py-24 lg:py-32 relative overflow-hidden" data-testid="our-story-section">
        <div className="container-x max-w-5xl mx-auto">
          <div className="text-center sm:text-left">
            <span className="overline !text-brand-turmeric mb-6 block" data-testid="our-story-overline">Our Story</span>
          </div>

          <Reveal className="mb-12 sm:mb-16 flex justify-center">
            <StoryPortrait />
          </Reveal>

          <Reveal delay={120}>
            <h2
              className="font-serif text-3xl sm:text-5xl lg:text-6xl leading-[1.1] mb-8 text-white"
              data-testid="our-story-heading"
            >
              Rooted in Andhra, Crafted for Every Home
            </h2>
          </Reveal>

          <Reveal delay={200}>
            <div className="space-y-5 text-base sm:text-[17px] lg:text-lg text-brand-cream/85 leading-[1.8] max-w-3xl" data-testid="our-story-body">
              <p>Sri Durga Foods was built on a simple belief — good food doesn&apos;t need shortcuts.</p>
              <p>What started as a family effort in Andhra Pradesh grew through one promise: authentic flavours, consistent quality and recipes people trust.</p>
              <p>Today, from traditional pickles and podis to spices and wood-pressed oils, every product carries the taste of home that Andhra families have enjoyed for years.</p>
              <p>No unnecessary claims.</p>
              <p>No changing recipes to chase trends.</p>
              <p>Just honest food, made the way it should be.</p>
            </div>
          </Reveal>

          <Reveal delay={280}>
            <div className="mt-12 flex justify-end">
              <button
                type="button"
                onClick={() => navigate("/shop")}
                className="inline-flex items-center gap-2 bg-brand-turmeric text-brand-dark font-medium tracking-wide rounded-full px-7 sm:px-9 py-3.5 sm:py-4 text-sm sm:text-base transition-transform active:scale-[0.98] hover:scale-[1.02]"
                data-testid="our-story-cta"
              >
                Explore The Pantry <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE SRI DURGA FOODS — REBUILT */}
      <section className="container-x py-20 sm:py-24 lg:py-32" data-testid="why-section">
        <div className="text-center mb-12 sm:mb-16 max-w-2xl mx-auto">
          <span className="overline mb-3 block" data-testid="why-overline">Why Choose Sri Durga Foods</span>
          <h2 className="font-serif text-4xl sm:text-5xl text-brand-dark leading-tight">Made with love. Built on trust.</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16 sm:mb-20">
          {WHY.map((w, i) => (
            <div
              key={w.title}
              className="bg-white border border-brand-line rounded-2xl p-7 hover:border-brand-chili/30 hover:-translate-y-1 transition-all duration-500 animate-fade-up opacity-0"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`why-${i}`}
            >
              <div className="w-12 h-12 rounded-full bg-brand-subtle flex items-center justify-center mb-5">
                <w.icon className="w-5 h-5 text-brand-chili" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl text-brand-dark mb-2">{w.title}</h3>
              <p className="text-sm text-brand-muted leading-relaxed">{w.desc}</p>
            </div>
          ))}
        </div>

        <Reveal>
          <figure className="relative max-w-4xl mx-auto px-4 sm:px-10 py-4">
            <span aria-hidden="true" className="absolute -top-2 left-0 font-serif text-7xl sm:text-8xl leading-none text-brand-turmeric/70 select-none">“</span>
            <blockquote
              className="font-serif italic font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.2] text-center tracking-[0.003em]"
              data-testid="why-quote"
            >
              <span className="text-brand-chili">Every jar carries the soul of </span>
              <span className="not-italic font-bold text-brand-dark">Andhra&apos;s</span>
              <span className="text-brand-chili"> legendary pickling tradition.</span>
            </blockquote>
            <span aria-hidden="true" className="absolute -bottom-10 right-0 font-serif text-7xl sm:text-8xl leading-none text-brand-turmeric/70 select-none">”</span>
          </figure>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="bg-brand-subtle py-20 sm:py-24 lg:py-32" data-testid="faq-section">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          <div className="md:col-span-4">
            <span className="overline mb-3 block">Frequently asked</span>
          </div>
          <div className="md:col-span-8">
            {FAQ.map((item, i) => (
              <FAQItem
                key={i}
                item={item}
                index={i}
                open={faqOpen === i}
                onToggle={() => setFaqOpen(faqOpen === i ? -1 : i)}
              />
            ))}
            <p className="text-sm text-brand-muted leading-relaxed mt-8" data-testid="faq-whatsapp-line">
              Still curious? Drop us a WhatsApp at <a href={waEnquiryUrl()} target="_blank" rel="noreferrer" className="text-brand-chili underline">{CONTACT.WHATSAPP_DISPLAY}</a>
            </p>
          </div>
        </div>
      </section>

      {/* BIG ORDERS / GET IN TOUCH */}
      <section className="container-x py-12 sm:py-14 lg:py-16" data-testid="contact-section">
        <div className="bg-brand-dark text-brand-cream rounded-3xl p-8 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <span className="overline !text-brand-turmeric mb-3 block">Get in touch</span>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-5">
              Big Orders?<br />Just Say the Word.
            </h2>
            <p className="text-brand-cream/75 text-sm sm:text-base leading-relaxed max-w-xl" data-testid="contact-description">
              We Love Bulk Orders. We Supply Authentic Andhra Pickles, Spices,
              Masalas, Traditional Powders, Flours and Wood Pressed Oils in
              Bulk Quantities for Hotels, Restaurants, Caterers and Retail
              Stores. WhatsApp us with your Needs.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <a
              href={waUrl("Hi Sri Durga Foods, I'd like to place a bulk order")}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 bg-[#25D366] text-white px-6 py-4 rounded-full hover:scale-[1.02] transition-transform"
              data-testid="contact-whatsapp"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2} />
              <span className="font-medium">WhatsApp · {CONTACT.WHATSAPP_DISPLAY}</span>
            </a>
            <a
              href={contact.tel}
              className="flex items-center gap-3 bg-brand-turmeric text-brand-dark px-6 py-4 rounded-full hover:scale-[1.02] transition-transform font-medium"
              data-testid="contact-call"
            >
              <Phone className="w-5 h-5" strokeWidth={2} />
              <span>Call · {contact.display}</span>
            </a>
            <a
              href={email.mailto("Order enquiry")}
              className="flex items-center gap-3 bg-white/10 backdrop-blur text-white px-6 py-4 rounded-full hover:bg-white/20 transition-colors"
              data-testid="contact-email"
            >
              <Mail className="w-5 h-5" strokeWidth={2} />
              <span>{email.address}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
